import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_MS = 60000;
const SECONDS_PER_CHAR = 0.38; // base scroll pace; divided by `speed`

// --- one CNBC-style scrolling row ------------------------------------------
function TickerRow({ label, segments, labelColor, emptyText, testid, speed = 1, trailingDots = false }) {
  const text = segments.length
    ? segments.map((s) => s.text).join("   ...   ")
    : emptyText;

  // Duration scales with content length; higher `speed` => faster scroll.
  const duration = useMemo(() => {
    const d = (text.length * SECONDS_PER_CHAR) / (speed || 1);
    return Math.max(25, Math.min(260, d));
  }, [text, speed]);

  return (
    <div className="pixticker-row" data-testid={testid}>
      <div className="pixticker-label" style={{ color: labelColor }}>
        {label}
      </div>
      <div className="pixticker-track">
        <div className="pixticker-scroll" style={{ animationDuration: `${duration}s` }}>
          <span className="pixticker-content">{renderSegments(segments, emptyText, trailingDots)}</span>
          <span className="pixticker-content" aria-hidden="true">
            {renderSegments(segments, emptyText, trailingDots)}
          </span>
        </div>
      </div>
    </div>
  );
}

function renderSegments(segments, emptyText, trailingDots) {
  if (!segments.length) return <span className="pixticker-empty">{emptyText}</span>;
  return segments.map((s, i) => (
    <span key={i} className={`pixticker-seg pixticker-${s.kind || "play"}${s.big ? " pixticker-big" : ""}`}>
      {s.text}
      {(i < segments.length - 1 || trailingDots) ? <span className="pixticker-dot">{"   ...   "}</span> : null}
    </span>
  ));
}

// --- two-row ticker bar (reusable: standalone page + embedded in widget) ----
export function PixTickerBar({ clientId = "TEST001", date, simulate = false, simPct, bomb, speed = 1 }) {
  const [data, setData] = useState({ plays: [], results: [], stats: { show: false } });
  const [error, setError] = useState("");
  const timer = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        let url = `${API}/quickpick/pixticker?clientId=${encodeURIComponent(clientId)}`;
        if (date) url += `&race_date=${encodeURIComponent(date)}`;
        if (simulate) url += `&simulate=true`;
        if (simPct != null) url += `&sim_pct=${simPct}`;
        if (bomb != null) url += `&bomb_threshold=${bomb}`;
        const res = await axios.get(url);
        setData(res.data);
        setError("");
      } catch (e) {
        setError(e?.response?.status === 403 ? "PixTicker not enabled for this client." : "Unable to load ticker.");
      }
    };
    load();
    timer.current = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer.current);
  }, [clientId, date, simulate, simPct, bomb]);

  const playSegs = (data.plays || []).map((p) => ({ text: p.text, kind: "play" }));

  const resultSegs = [];
  const st = data.stats || {};
  if (st.show && st.exacta?.graded) {
    resultSegs.push({
      text: `Exacta Win Rate \u2014 ${st.exacta.graded} races \u2014 ${st.exacta.rate}%`,
      kind: "stat",
    });
  }
  (data.results || []).forEach((r) => resultSegs.push({ text: r.text, kind: r.kind, big: r.big }));

  if (error) {
    return (
      <div className="pixticker-bar" data-testid="pixticker-error">
        <style>{PIXTICKER_CSS}</style>
        <div className="pixticker-row">
          <div className="pixticker-label" style={{ background: "#7f1d1d" }}>PixTicker</div>
          <div className="pixticker-track"><span className="pixticker-empty">{error}</span></div>
        </div>
      </div>
    );
  }

  return (
    <div className="pixticker-bar" data-testid="pixticker-bar">
      <style>{PIXTICKER_CSS}</style>
      <TickerRow
        label="UPCOMING PLAYS"
        segments={playSegs}
        labelColor="#39ff14"
        emptyText="... awaiting plays ..."
        testid="pixticker-plays-row"
        speed={speed}
        trailingDots
      />
      <TickerRow
        label="RESULTS"
        segments={resultSegs}
        labelColor="#faff00"
        emptyText="... awaiting results ..."
        testid="pixticker-results-row"
        speed={speed}
      />
    </div>
  );
}

// --- standalone embeddable page (route: /pixticker) ------------------------
export default function PixTickerPage() {
  const params = new URLSearchParams(window.location.search);
  const clientId = params.get("clientId") || "TEST001";
  const date = params.get("date") || undefined;
  const simulate = params.get("simulate") === "true";
  const simPct = params.get("sim_pct") != null ? parseFloat(params.get("sim_pct")) : undefined;
  const bomb = params.get("bomb") != null ? parseFloat(params.get("bomb")) : undefined;
  const speed = params.get("speed") != null ? parseFloat(params.get("speed")) : 1;
  const brand = params.get("brand"); // null = no panel (bar-only); "" or text = show panel
  const wrapStyle = brand !== null
    ? { minHeight: "100vh", background: "#000", display: "flex", flexDirection: "column" }
    : { background: "#000" };

  return (
    <div style={wrapStyle}>
      <style>{"html,body{margin:0;padding:0;background:#000;}"}</style>
      <PixTickerBar clientId={clientId} date={date} simulate={simulate} simPct={simPct} bomb={bomb} speed={speed} />
      {brand !== null && (
        <div className="pixticker-brand-panel" data-testid="pixticker-brand-panel">
          <style>{BRAND_CSS}</style>
          <div className="pixticker-brand-inner">
            <div className="pixticker-brand-title">MyRaceBookPage Here</div>
            <img
              className="pixticker-brand-img"
              src="https://images.pexels.com/photos/6818590/pexels-photo-6818590.jpeg"
              alt="Horse racing"
            />
          </div>
        </div>
      )}
    </div>
  );
}

const BRAND_CSS = `
.pixticker-brand-panel {
  flex:1 1 auto; display:flex; align-items:center; justify-content:center;
  background:#fff; min-height:160px; padding:24px;
  font-family:Arial, sans-serif; border-top:1px solid #e5e7eb;
}
.pixticker-brand-inner { display:flex; flex-direction:column; align-items:center; gap:18px; }
.pixticker-brand-title { color:#111; font-size:26px; font-weight:800; letter-spacing:0.5px; }
.pixticker-brand-img { max-width:520px; width:100%; height:auto; border-radius:10px; box-shadow:0 6px 20px rgba(0,0,0,0.15); }
`;

const PIXTICKER_CSS = `
.pixticker-bar { width: 100%; font-family: 'Courier New', Consolas, monospace; background:#000; }
.pixticker-row { display:flex; align-items:stretch; border-bottom:1px solid #111; height:38px; }
.pixticker-row:last-child { border-bottom:none; }
.pixticker-label {
  flex:0 0 auto; min-width:150px; display:flex; align-items:center; justify-content:center;
  padding:0 14px; background:#000; font-weight:800; font-size:13px; letter-spacing:1px;
  white-space:nowrap; text-transform:uppercase; border-right:1px solid #1f2937;
}
.pixticker-track { flex:1 1 auto; overflow:hidden; white-space:nowrap; position:relative; background:#000; display:flex; align-items:center; }
.pixticker-scroll { display:inline-flex; white-space:nowrap; will-change:transform; animation-name:pixticker-marquee; animation-timing-function:linear; animation-iteration-count:infinite; }
.pixticker-content { padding-right:48px; }
@keyframes pixticker-marquee { from { transform:translateX(0); } to { transform:translateX(-50%); } }
.pixticker-seg { font-weight:700; font-size:15px; }
.pixticker-play { color:#7fff00; }
.pixticker-stat { color:#fde047; font-weight:800; }
.pixticker-exacta { color:#faff00; }
.pixticker-trifecta { color:#22d3ee; }
.pixticker-win { color:#fb923c; }
.pixticker-dot { color:#e5e7eb; }
.pixticker-empty { color:#6b7280; font-style:italic; padding-left:16px; font-size:14px; }
.pixticker-big {
  display:inline-block; color:#fde047 !important; font-weight:900; font-size:16px;
  padding:0 4px;
  animation: pixticker-shake 0.45s linear infinite, pixticker-glow 0.9s ease-in-out infinite alternate;
}
@keyframes pixticker-shake {
  0%,100% { transform:translate(0,0) rotate(0); }
  20% { transform:translate(-1.5px,1px) rotate(-2deg); }
  40% { transform:translate(1.5px,-1px) rotate(2deg); }
  60% { transform:translate(-1.5px,-1px) rotate(-2deg); }
  80% { transform:translate(1.5px,1px) rotate(2deg); }
}
@keyframes pixticker-glow {
  from { text-shadow:0 0 4px #fde047, 0 0 8px #f59e0b; }
  to { text-shadow:0 0 10px #fbbf24, 0 0 20px #f59e0b, 0 0 30px #f97316; }
}
.pixticker-track:hover .pixticker-scroll { animation-play-state:paused; }
`;
